import { useState } from 'react';
import { all } from '@/api/admin/provider';
import { DataTable } from '@/components/ui/custom/data-table';
import { TablePagination } from '@/components/ui/custom/table-pagination';
import { SearchBar } from '@/components/ui/SearchBar/SearchBar';
import { useSearch } from '@/hooks/use-search';
import { keepPreviousData, Updater, useQuery } from '@tanstack/react-query';
import {
	ColumnFiltersState,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	PaginationState,
	SortingState,
	useReactTable,
	VisibilityState,
} from '@tanstack/react-table';

import { getColumns } from './columns';
import { AddImportTypeProvider } from './sheet-add';

interface ProviderTableProps {
	importTypeId: number;
}

export function ProviderTable({ importTypeId }: ProviderTableProps) {
	const { searchTerm, setSearchTerm, pagination, setPagination } = useSearch();
	const [sorting, setSorting] = useState<SortingState>([
		{
			id: 'wsPrefixComplocProviderXrefId',
			desc: true,
		},
	]);
	const { data, isFetching } = useQuery({
		queryFn: () =>
			all(pagination, sorting, importTypeId, {
				searchTerm: searchTerm,
			}),
		queryKey: [`provider-${importTypeId}`, pagination, sorting, searchTerm],
		placeholderData: keepPreviousData,
		staleTime: 30000,
	});

	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
	const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
	const [rowSelection, setRowSelection] = useState({});
	const [globalFilter, setGlobalFilter] = useState<any>([]);

	const handlePageChange = (
		updater: Updater<PaginationState, PaginationState>,
	) => {
		setPagination((prev) => {
			const newPagination =
				typeof updater === 'function' ? updater(prev) : updater;

			return newPagination; // Allow page change
		});
	};
	const table = useReactTable({
		data: data?.data ?? [],
		columns: getColumns(importTypeId),
		rowCount: data?.total ?? 0,
		manualPagination: true,
		manualSorting: true,
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onColumnVisibilityChange: setColumnVisibility,
		onRowSelectionChange: setRowSelection,
		onGlobalFilterChange: setGlobalFilter,
		onPaginationChange: handlePageChange,
		state: {
			sorting,
			columnFilters,
			columnVisibility,
			rowSelection,
			globalFilter,
			pagination,
		},
	});

	return (
		<div className="h-1/2 w-full">
			<div className="flex items-center justify-end gap-4 py-4">
				<SearchBar
					placeholder="Search..."
					onSearch={setSearchTerm}
					isLoading={isFetching}
				/>
				<div className="flex items-center gap-2">
					<AddImportTypeProvider importTypeId={importTypeId} />
				</div>
			</div>

			<DataTable
				table={table}
				isFetching={isFetching}
				noResultsMessage="No providers found."
			/>

			<TablePagination table={table} isFetching={isFetching} />
		</div>
	);
}
